<template>
    <div>
        <div class="profile-hero">
            <div class="profile-avatar-wrap">
                <div class="profile-avatar">
                    <img v-if="photo" :src="photo" alt=""/>
                    <span v-else>{{ initials }}</span>
                </div>
                <button class="profile-avatar-edit" type="button" :aria-label="$t('changePhoto')"
                        @click="triggerPhoto">
                    <i class="fa-solid fa-camera"></i>
                </button>
                <input ref="photoInput" class="profile-photo-input" type="file" accept="image/*"
                       @change="onPhotoSelected"/>
            </div>
            <div class="profile-name">{{ fullName }}</div>
            <div class="profile-email">{{ $store.state.email }}</div>
            <button v-if="photo" class="profile-photo-remove" type="button" @click="removePhoto">
                <i class="fa-solid fa-trash"></i> {{ $t('removePhoto') }}
            </button>
        </div>

        <div class="card">
            <div class="card-title">{{ $t('activeMembership') }}</div>
            <div v-if="person && person.active_membership" class="membership-pill">
                <i class="fa-solid fa-id-card"></i>
                <span>
                    {{ person.active_membership }}
                    <template v-if="person.active_membership_end">
                        · {{ $t('membershipUntil') }} {{ formatDate(person.active_membership_end) }}
                    </template>
                </span>
            </div>
            <div v-else class="membership-none">
                <i class="fa-regular fa-circle"></i>
                <span>{{ $t('noActiveMembership') }}</span>
            </div>
        </div>

        <div class="card">
            <div class="card-title">{{ $t('account') }}</div>
            <button v-if="$store.state.clients.length > 1" class="list-row" @click="switchClient">
                <i class="fa-solid fa-arrows-rotate"></i>
                <span>{{ $t('switchClient') }}</span>
                <i class="fa-solid fa-chevron-right chev"></i>
            </button>
            <button class="list-row" @click="changePassword">
                <i class="fa-solid fa-key"></i>
                <span>{{ $t('changePassword') }}</span>
                <i class="fa-solid fa-chevron-right chev"></i>
            </button>
            <button class="list-row danger" @click="logout">
                <i class="fa-solid fa-right-from-bracket"></i>
                <span>{{ $t('logout') }}</span>
            </button>
        </div>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    name: 'ProfileView',
    computed: {
        person() {
            return this.$store.state.person;
        },
        fullName() {
            if (!this.person) return '';
            return (this.person.first_name + ' ' + this.person.last_name).trim();
        },
        initials() {
            if (!this.person) return '';
            const first = (this.person.first_name || '').charAt(0);
            const last = (this.person.last_name || '').charAt(0);
            return (first + last).toUpperCase() || '?';
        },
        // The profile photo comes from the server as a data URI (or null when there is none).
        photo() {
            return this.person ? this.person.photo : null;
        }
    },
    mounted() {
        this.load();
    },
    methods: {
        async load() {
            const response = await this.$api.get('profile');
            if (response.success) {
                this.$store.commit('setPerson', response.data.person);
            }
        },
        formatDate(date) {
            return moment(date, 'YYYY-MM-DD').format('D.M.YYYY.');
        },
        triggerPhoto() {
            this.$refs.photoInput.click();
        },
        async onPhotoSelected(event) {
            const file = event.target.files[0];
            // Allow re-picking the same file later by clearing the input.
            event.target.value = '';
            if (!file) return;
            if (!file.type || !file.type.startsWith('image/')) {
                this.$store.commit('showToast', { message: this.$t('invalidImage'), type: 'error' });
                return;
            }
            const response = await this.$api.upload('profile/photo', file);
            if (response.success) {
                this.$store.commit('setPerson', { ...this.person, photo: response.data.photo });
                this.$store.commit('showToast', { message: this.$t('photoUpdated'), type: 'success' });
            } else {
                const message = response.data && response.data.error === 'invalid_image'
                    ? this.$t('invalidImage') : this.$t('genericError');
                this.$store.commit('showToast', { message, type: 'error' });
            }
        },
        async removePhoto() {
            const confirmed = await this.$confirm({
                title: this.$t('confirmRemovePhotoTitle'),
                message: this.$t('confirmRemovePhotoMessage'),
                confirmLabel: this.$t('removePhoto')
            });
            if (!confirmed) return;
            const response = await this.$api.post('profile/photo/remove');
            if (response.success) {
                this.$store.commit('setPerson', { ...this.person, photo: null });
                this.$store.commit('showToast', { message: this.$t('photoRemoved'), type: 'success' });
            } else {
                this.$store.commit('showToast', { message: this.$t('genericError'), type: 'error' });
            }
        },
        switchClient() {
            this.$router.push({ name: 'select-client' });
        },
        async changePassword() {
            const confirmed = await this.$confirm({
                title: this.$t('confirmPasswordResetTitle'),
                message: this.$t('confirmPasswordResetMessage'),
                confirmLabel: this.$t('sendChangeLink')
            });
            if (!confirmed) return;
            const response = await this.$api.post('password/change', {}, { client: false });
            if (response.success) {
                this.$store.commit('showToast', { message: this.$t('changeLinkSent'), type: 'success' });
            } else {
                this.$store.commit('showToast', { message: this.$t('genericError'), type: 'error' });
            }
        },
        async logout() {
            await this.$api.post('logout', {}, { client: false });
            this.$store.commit('reset');
            this.$router.push({ name: 'login' });
        }
    }
};
</script>
